import { collection, query, where } from "firebase/firestore";
import { useMemo } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { useAuth } from "../contexts/AuthContext";
import { Post } from "../models/Post";
import { postConverter } from "../services/converters";
import { db } from "../services/Firebase";

interface UseScheduledPostsOptions {
  /** When set, drafts belonging to this batch are merged in (batch review flow). */
  batchId?: string | null;
}

interface UseScheduledPostsResult {
  /** Every non-draft post, plus this batch's drafts when batchId is set. */
  posts: Post[];
  /** Only the batch's posts, empty when no batchId. */
  batchPosts: Post[];
  loading: boolean;
  error?: Error;
}

/**
 * Shared source of truth for "posts that occupy a slot on the calendar".
 * Consumed by both the calendar page and the schedule picker so they agree on
 * what is already booked.
 */
export function useScheduledPosts(
  options: UseScheduledPostsOptions = {}
): UseScheduledPostsResult {
  const { batchId } = options;
  const { currentUser, activeWorkspaceId } = useAuth();

  const [postsSnapshot, loading, error] = useCollection(
    currentUser
      ? query(
          collection(db, "posts").withConverter(postConverter),
          where("userId", "==", activeWorkspaceId),
          where("status", "!=", "draft")
        )
      : null
  );

  const [batchPostsSnapshot] = useCollection(
    batchId && currentUser
      ? query(
          collection(db, "posts").withConverter(postConverter),
          where("userId", "==", activeWorkspaceId),
          where("batchId", "==", batchId)
        )
      : null
  );

  const batchPosts = useMemo(() => {
    if (!batchId || !batchPostsSnapshot) return [];
    return batchPostsSnapshot.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id }) as Post
    );
  }, [batchId, batchPostsSnapshot]);

  const posts = useMemo(() => {
    const mainPosts =
      postsSnapshot?.docs.map((doc) => ({ ...doc.data(), id: doc.id }) as Post) ||
      [];

    if (batchPosts.length === 0) return mainPosts;

    const mainPostIds = new Set(mainPosts.map((p) => p.id));
    return [...mainPosts, ...batchPosts.filter((p) => !mainPostIds.has(p.id))];
  }, [postsSnapshot, batchPosts]);

  return { posts, batchPosts, loading, error };
}
